import { round2Decimals, round6Decimals } from "../../../../Components/Items";

// Calculo de impuestos, retenciones y totales de las partidas de un pedido.
//
// Es un port literal de AgregarPedido.recalcularInsumosEnBatch (por partida) y
// del agregado que hace AgregarPedido.onFinish (por pedido). Se copio en lugar de
// reutilizarse porque alla son metodos de una clase de 3,600 lineas atados al
// formulario. Cualquier cambio de reglas fiscales hay que reflejarlo en ambos:
// erp/src/Pages/Panel/Pedidos/AgregarPedido/AgregarPedido.js

/**
 * Calcula una partida a partir del insumo del catalogo y los datos capturados.
 * Devuelve el mismo juego de campos que el formulario de Agregar Pedido manda
 * al backend en cada renglon de `insumos`.
 */
export function calcularPartida({
  insumo,
  cliente,
  precioUnitario,
  cantidad,
  descuento = 0,
  tipoDeCambio = 1,
  quitarImpuestos = false,
}) {
  const precio = Number(precioUnitario) || 0;
  const cantidadNumero = Number(cantidad) || 0;
  const impuestosInsumo = insumo?.impuestos || [];
  const retencionesInsumo = insumo?.retenciones || [];

  // Precio unitario con impuestos: solo informativo, se calcula sobre el precio
  // sin descuento igual que en el formulario
  let impuestosSobrePrecio = 0;
  if (!quitarImpuestos) {
    impuestosInsumo.forEach((valueImpuesto) => {
      let totalImpuesto;
      if (valueImpuesto.tasaCuota === 1) {
        totalImpuesto = precio * (valueImpuesto.monto / 100);
      } else if (valueImpuesto.tasaCuota === 2) {
        totalImpuesto = (precio + impuestosSobrePrecio) * (valueImpuesto.monto / 100);
      } else if (valueImpuesto.tasaCuota === 3) {
        totalImpuesto = valueImpuesto.monto / tipoDeCambio;
      } else {
        totalImpuesto = 0;
      }
      impuestosSobrePrecio += totalImpuesto;
    });
  }

  const subtotalBruto = round6Decimals(cantidadNumero * precio);
  const totalDescuento = round6Decimals(subtotalBruto * (descuento / 100));
  const subtotal = round6Decimals(subtotalBruto - totalDescuento);

  let totalImpuestos = 0;
  let arrayImpuestos = [];
  if (!quitarImpuestos) {
    arrayImpuestos = impuestosInsumo.map((valueImpuesto) => {
      let totalImpuesto;
      let baseImpuesto;
      let montoImpuesto;
      if (valueImpuesto.tasaCuota === 1) {
        totalImpuesto = round6Decimals(subtotal * (valueImpuesto.monto / 100));
        baseImpuesto = subtotal;
        montoImpuesto = round6Decimals(valueImpuesto.monto / 100);
      } else if (valueImpuesto.tasaCuota === 2) {
        totalImpuesto = round6Decimals((subtotal + totalImpuestos) * (valueImpuesto.monto / 100));
        baseImpuesto = subtotal + totalImpuestos;
        montoImpuesto = round6Decimals(valueImpuesto.monto / 100);
      } else if (valueImpuesto.tasaCuota === 3) {
        totalImpuesto = round6Decimals((cantidadNumero * valueImpuesto.monto) / tipoDeCambio);
        baseImpuesto = cantidadNumero;
        montoImpuesto = round6Decimals(valueImpuesto.monto / tipoDeCambio);
      } else {
        totalImpuesto = 0;
        baseImpuesto = subtotal;
        montoImpuesto = 0;
      }
      totalImpuestos += totalImpuesto;
      return {
        tasaCuota: valueImpuesto.tasaCuota,
        impuesto: valueImpuesto.impuesto,
        montoImpuesto,
        local: valueImpuesto.local,
        baseImpuesto,
        descripcionFactura: valueImpuesto.descripcionFactura,
        totalImpuestos: totalImpuesto,
      };
    });
  }

  let totalRetenciones = 0;
  const arrayRetenciones = [];
  if (!quitarImpuestos && cliente) {
    const aplicables = [
      ...(cliente.retenciones || []),
      ...retencionesInsumo.filter(
        (valueRetencion) =>
          (valueRetencion.aplicaVenta === 1 && !cliente.persona) ||
          (valueRetencion.aplicaVenta === 2 && cliente.persona) ||
          valueRetencion.aplicaVenta === 3
      ),
    ];
    aplicables.forEach((valueRetencion) => {
      let totalRetencion;
      let baseRetencion;
      if (valueRetencion.tasaCuota === 1) {
        totalRetencion = round6Decimals(subtotal * (valueRetencion.monto / 100));
        baseRetencion = subtotal;
      } else if (valueRetencion.tasaCuota === 2) {
        totalRetencion = round6Decimals((subtotal + totalRetenciones) * (valueRetencion.monto / 100));
        // Se conserva tal cual esta en Agregar Pedido: la base usa impuestos y el
        // total usa retenciones. Cambiarlo aqui descuadraria contra esa pantalla.
        baseRetencion = subtotal + totalImpuestos;
      } else {
        totalRetencion = 0;
        baseRetencion = subtotal;
      }
      totalRetenciones += totalRetencion;
      arrayRetenciones.push({
        tasaCuota: valueRetencion.tasaCuota,
        impuesto: valueRetencion.impuesto,
        montoRetencion: valueRetencion.monto,
        local: valueRetencion.local,
        baseRetencion,
        descripcionFactura: valueRetencion.descripcionFactura,
        totalRetenciones: totalRetencion,
      });
    });
  }

  return {
    precioUnitario: precio,
    precioUnitarioConImpuestos: round2Decimals(precio + impuestosSobrePrecio),
    cantidad: cantidadNumero,
    descuento,
    descuentoCantidad: 0,
    subtotal,
    impuestos: totalImpuestos,
    arrayImpuestos,
    retenciones: totalRetenciones,
    arrayRetenciones,
    total: round2Decimals(subtotal + totalImpuestos),
  };
}

// Acumula un impuesto o retencion en el arreglo del pedido, sumando cuando ya
// existe uno con la misma descripcion, tasa e impuesto
const acumular = (acumulado, valor, campoTotal, campoBase) => {
  const index = acumulado.findIndex(
    (anterior) =>
      anterior.descripcionFactura === valor.descripcionFactura &&
      anterior.tasaCuota === valor.tasaCuota &&
      anterior.impuesto === valor.impuesto
  );
  if (index === -1) {
    acumulado.push({ ...valor });
    return;
  }
  acumulado.splice(index, 1, {
    ...valor,
    [campoTotal]: acumulado[index][campoTotal] + valor[campoTotal],
    [campoBase]: acumulado[index][campoBase] + valor[campoBase],
  });
};

/**
 * Agrega las partidas de un pedido en los totales que espera
 * pedidos/agregarPedido. Mismo orden de operaciones que AgregarPedido.onFinish.
 */
export function agregarTotalesPedido(partidas) {
  let subtotal = 0;
  let total = 0;
  let cuentasCliente = 0;
  let arrayImpuestos = [];
  const arrayRetenciones = [];

  partidas.forEach((partida) => {
    total += partida.subtotal;
    subtotal += partida.subtotal;

    (partida.arrayImpuestos || []).forEach((valueImpuesto) => {
      acumular(arrayImpuestos, valueImpuesto, "totalImpuestos", "baseImpuesto");
    });

    (partida.arrayRetenciones || []).forEach((valueRetencion) => {
      cuentasCliente += round2Decimals(valueRetencion.totalRetenciones);
      acumular(arrayRetenciones, valueRetencion, "totalRetenciones", "baseRetencion");
    });
  });

  arrayImpuestos = arrayImpuestos.map((valueImpuesto) => {
    total += round2Decimals(valueImpuesto.totalImpuestos);
    return { ...valueImpuesto, totalImpuestos: round2Decimals(valueImpuesto.totalImpuestos) };
  });

  return {
    subtotal,
    total,
    arrayImpuestos,
    arrayRetenciones,
    cuentasCliente: round2Decimals(cuentasCliente),
    totalPagar: round2Decimals(total - cuentasCliente),
  };
}

/**
 * Precio unitario que le toca a un insumo: primero el configurado para el
 * cliente en Insumos por Cliente y, si no tiene, el de la lista de precios
 * seleccionada en la pantalla.
 */
export function obtenerPrecioUnitario(insumo, precioId) {
  if (insumo?.precioClienteAlternativo) return insumo.precioClienteAlternativo;
  const precio = (insumo?.precios || []).find(
    (item) => item.precioId?.toString() === precioId?.toString()
  );
  return precio ? precio.precio : 0;
}
